/**
 * Sieve — shared `primes` engine (spec §14).
 *
 * ONE implementation, ONE source of truth. This module is imported by:
 *   - the queue generator (src/generator),
 *   - the curation tooling,
 *   - the live client (src/game, src/ui).
 *
 * Everything here is a mathematical fact about integers in the game's fixed
 * range 100–999 (§4.1). No third-party content, ever (§13.1).
 */

/** Fixed number range for every cell (§4.1, §17.3). */
export const MIN_NUMBER = 100;
export const MAX_NUMBER = 999;

/**
 * Primes small enough to fully test any three-digit number by trial division:
 * a composite < 1000 always has a prime factor ≤ √999 < 32 (§4.1). Testing
 * 2..31 is therefore both sufficient and the exact mental toolkit the game
 * teaches (§6.5).
 */
export const TEST_PRIMES = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31] as const;

/**
 * Deterministic primality by trial division up to √n (§5.2 rule 2).
 * Correct for all non-negative integers, though the game only uses 100–999.
 */
export function isPrime(n: number): boolean {
  if (!Number.isInteger(n) || n < 2) return false;
  if (n < 4) return true; // 2, 3
  if (n % 2 === 0 || n % 3 === 0) return false;
  // 6k±1 wheel: every prime > 3 is of this form.
  for (let i = 5; i * i <= n; i += 6) {
    if (n % i === 0 || n % (i + 2) === 0) return false;
  }
  return true;
}

/**
 * Smallest prime factor of a composite n. Returns n itself when n is prime,
 * and NaN for n < 2. This is what drives trap classification (§4.2).
 */
export function smallestPrimeFactor(n: number): number {
  if (!Number.isInteger(n) || n < 2) return Number.NaN;
  if (n % 2 === 0) return 2;
  for (let i = 3; i * i <= n; i += 2) {
    if (n % i === 0) return i;
  }
  return n; // prime
}

/**
 * Full prime factorisation as ascending prime factors with multiplicity.
 * e.g. factorize(391) → [17, 23]; factorize(529) → [23, 23]; factorize(360) →
 * [2, 2, 2, 3, 3, 5]. Powers the "wrong tap" reveal (§6.3).
 */
export function factorize(n: number): number[] {
  const factors: number[] = [];
  if (!Number.isInteger(n) || n < 2) return factors;
  let remaining = n;
  for (let d = 2; d * d <= remaining; d += d === 2 ? 1 : 2) {
    while (remaining % d === 0) {
      factors.push(d);
      remaining = remaining / d;
    }
  }
  if (remaining > 1) factors.push(remaining);
  return factors;
}

/**
 * Human-facing factorisation string shown beneath a wrongly-tapped composite
 * (§6.3), e.g. "17×23", "23²", "2³×3²×5". Uses a middle dot? No — the spec
 * shows `17×23`, so we use the multiplication sign.
 */
export function factorString(n: number): string {
  const factors = factorize(n);
  if (factors.length === 0) return String(n);
  // Group equal primes into powers for a compact, readable label.
  const groups: Array<{ base: number; exp: number }> = [];
  for (const f of factors) {
    const last = groups[groups.length - 1];
    if (last && last.base === f) last.exp += 1;
    else groups.push({ base: f, exp: 1 });
  }
  return groups
    .map((g) => (g.exp === 1 ? String(g.base) : `${g.base}${superscript(g.exp)}`))
    .join('×');
}

const SUPERSCRIPTS = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'] as const;
function superscript(exp: number): string {
  return String(exp)
    .split('')
    .map((d) => SUPERSCRIPTS[Number(d)] ?? d)
    .join('');
}

/**
 * Composite class, keyed by smallest prime factor (§4.2). Primes are `null`.
 *
 *   gimme : SPF ∈ {2,3,5}  — free elimination from last digit / digit sum
 *   t1    : SPF = 7        — first non-trivial check
 *   t2    : SPF ∈ {11,13}  — alternating-sum or /13 test
 *   t3    : SPF ≥ 17       — must check all the way to 31
 */
export type CompositeClass = 'gimme' | 't1' | 't2' | 't3';
export type NumberClass = 'prime' | CompositeClass;

export function classify(n: number): NumberClass {
  if (isPrime(n)) return 'prime';
  const spf = smallestPrimeFactor(n);
  if (spf === 2 || spf === 3 || spf === 5) return 'gimme';
  if (spf === 7) return 't1';
  if (spf === 11 || spf === 13) return 't2';
  return 't3'; // spf >= 17
}

export type TrapClass = 't1' | 't2' | 't3';
export function isTrap(cls: NumberClass): cls is TrapClass {
  return cls === 't1' || cls === 't2' || cls === 't3';
}

/** All primes in [MIN_NUMBER, MAX_NUMBER]. There are 143 of them (§4.1). */
export function primesInRange(min = MIN_NUMBER, max = MAX_NUMBER): number[] {
  const out: number[] = [];
  for (let n = min; n <= max; n++) if (isPrime(n)) out.push(n);
  return out;
}

/** All composites in range, grouped by class — the generator's raw material. */
export function compositesByClass(
  min = MIN_NUMBER,
  max = MAX_NUMBER,
): Record<CompositeClass, number[]> {
  const out: Record<CompositeClass, number[]> = { gimme: [], t1: [], t2: [], t3: [] };
  for (let n = min; n <= max; n++) {
    const cls = classify(n);
    if (cls !== 'prime') out[cls].push(n);
  }
  return out;
}

/**
 * A prime sits "near" a T3 trap when it lies within ±2 of one present in the
 * same grid (§4.5 soft preference), e.g. 719 next to 713 = 23×31.
 */
export function isNearT3(prime: number, gridNumbers: readonly number[]): boolean {
  for (const other of gridNumbers) {
    if (other === prime) continue;
    if (Math.abs(other - prime) <= 2 && classify(other) === 't3') return true;
  }
  return false;
}
